import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { GpsTrip } from "./use-gps-trips";

function calculateDuration(start: string | null, end: string | null): number {
  if (!start || !end) return 0;
  const diff = new Date(end).getTime() - new Date(start).getTime();
  return Math.round(diff / (1000 * 60));
}

export function useVehicleTrips(vehicleId?: string, companyId?: string) {
  return useQuery({
    queryKey: ['vehicle-trips', vehicleId, companyId],
    queryFn: async () => {
      if (!vehicleId || !companyId) return [];
      
      const { data, error } = await supabase
        .from('fleet_trips')
        .select(`
          id,
          origin,
          destination,
          eta_start,
          eta_end,
          start_at,
          end_at,
          status,
          distance_planned_km,
          distance_actual_km,
          origin_lat,
          origin_lng,
          destination_lat,
          destination_lng,
          vehicle_id,
          fleet_drivers(id, name),
          fleet_vehicles(id, plate, make, model)
        `)
        .eq('company_id', companyId)
        .eq('vehicle_id', vehicleId)
        .order('eta_start', { ascending: false });

      if (error) {
        console.error('Error fetching vehicle trips:', error);
        throw error;
      }

      // Adaptar para formato compatível com GpsTrip
      return (data || []).map(trip => ({
        id: trip.id,
        device_id: '',
        vehicle_id: trip.vehicle_id,
        start_time: trip.start_at || trip.eta_start,
        end_time: trip.end_at || trip.eta_end,
        distance_km: trip.distance_actual_km || trip.distance_planned_km || 0,
        duration_minutes: calculateDuration(trip.start_at || trip.eta_start, trip.end_at || trip.eta_end),
        idle_time_minutes: 0,
        start_location: {
          lat: trip.origin_lat || 0,
          lon: trip.origin_lng || 0,
          address: { formatted: trip.origin }
        },
        end_location: {
          lat: trip.destination_lat || 0,
          lon: trip.destination_lng || 0,
          address: { formatted: trip.destination }
        },
        vehicle_plate: trip.fleet_vehicles?.plate,
        vehicle_make: trip.fleet_vehicles?.make,
        vehicle_model: trip.fleet_vehicles?.model,
        driver_name: trip.fleet_drivers?.name,
        status: trip.status
      })) as GpsTrip[];
    },
    enabled: !!vehicleId && !!companyId
  });
}
